<template>
  <v-container fluid class="settings-page">
    <!-- Header -->
    <div class="settings-header">
      <h1 class="settings-header__title">Settings</h1>
      <p class="settings-header__subtitle">Configure and manage HRDC E-Invoicing system settings</p>
    </div>

    <!-- Module-Specific Settings -->
    <div class="settings-section">
      <div class="settings-section__label">
        <v-icon size="14" color="#10b981" class="mr-1">mdi-cube-outline</v-icon>
        Module-Specific
      </div>
      <div class="settings-grid">
        <div
          v-for="(item, i) in moduleItems"
          :key="'m-' + i"
          class="settings-card"
          @click="navigateSetting(item.route)"
        >
          <div class="settings-card__content">
            <div class="settings-card__top">
              <div class="settings-card__icon" :style="{ background: item.accentColor + '14' }">
                <v-icon :color="item.accentColor" size="24">{{ item.mdiIcon }}</v-icon>
              </div>
              <div class="settings-card__info">
                <h3 class="settings-card__name">{{ item.title }}</h3>
                <p class="settings-card__desc">{{ item.description }}</p>
              </div>
            </div>
            <div class="settings-card__bottom">
              <span class="settings-card__action">Open Settings</span>
              <v-icon size="16" class="settings-card__arrow">mdi-arrow-right</v-icon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Shared Global Settings -->
    <div class="settings-section" v-if="sharedItems.length">
      <div class="settings-section__label">
        <v-icon size="14" color="var(--app-text-muted)" class="mr-1">mdi-earth</v-icon>
        Shared (Global)
        <span class="settings-section__hint">These settings are shared across all event modules</span>
      </div>
      <div class="settings-grid">
        <div
          v-for="(item, i) in sharedItems"
          :key="'s-' + i"
          class="settings-card"
          @click="navigateSetting(item.route)"
        >
          <div class="settings-card__content">
            <div class="settings-card__top">
              <div class="settings-card__icon" :style="{ background: item.accentColor + '14' }">
                <v-icon :color="item.accentColor" size="24">{{ item.mdiIcon }}</v-icon>
              </div>
              <div class="settings-card__info">
                <h3 class="settings-card__name">{{ item.title }}</h3>
                <p class="settings-card__desc">{{ item.description }}</p>
              </div>
            </div>
            <div class="settings-card__bottom">
              <span class="settings-card__action">Open Settings</span>
              <v-icon size="16" class="settings-card__arrow">mdi-arrow-right</v-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";

export default {
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  props: ["params"],
  data: () => ({
    model: new Model(),
    service: new Service(),
    moduleItems: [],
    sharedItems: [],
  }),
  async created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Module-specific settings only
    this.moduleItems = [
      {
        accentColor: "#10b981",
        title: "Event",
        description: "Manage events for this module",
        route: this.eventRoute("SettingsEvents"),
        mdiIcon: "mdi-calendar-star",
      },
    ];

    // Shared settings moved to "Event Settings" service
    this.sharedItems = [];

    // Perform first check on user role, before enter (to prevent salesperson enter to unwanted view).
    this.roleMiddleware(this.auth.roles, this.service.key);
  },
  methods: {
    navigateSetting(route) {
      this.$router.push({
        name: route,
      });
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .settings-page {
  background-color: var(--app-page);
}

.dark-mode .settings-header__title {
  color: var(--app-text);
}

.dark-mode .settings-header__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .settings-section__label {
  color: var(--app-text-muted);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .settings-section__hint {
  color: var(--app-text-faint);
}

.dark-mode .settings-card {
  background: var(--app-card);
}

.dark-mode .settings-card__name {
  color: var(--app-text);
}

.dark-mode .settings-card__desc {
  color: var(--app-text-muted);
}

.dark-mode .settings-card__action {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

.settings-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 36px 44px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Header */
.settings-header {
  margin-bottom: 32px;
}

.settings-header__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
}

.settings-header__subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
}

/* Sections */
.settings-section {
  margin-bottom: 28px;
}

.settings-section__label {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.settings-section__hint {
  font-weight: 500;
  font-size: 0.7rem;
  color: #94a3b8;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 8px;
}

/* Grid */
.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}

/* Card */
.settings-card {
  background: #ffffff; border: 1px solid rgba(226, 232, 240, 0.5);
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  display: flex;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
}

.settings-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

/* Accent */


/* Content */
.settings-card__content {
  flex: 1;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Top */
.settings-card__top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.settings-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #e0e3e7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.settings-card__info {
  flex: 1;
  min-width: 0;
}

.settings-card__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}

.settings-card__desc {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
}

/* Bottom */
.settings-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.settings-card__action {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.settings-card__arrow {
  /* !important throughout: Vuetify's `.theme--light.v-icon` is (0,2,1) and
     would otherwise outrank these scoped class rules. */
  color: #c0c7cf !important;
  transition: all 0.2s ease;
}

.settings-card:hover .settings-card__arrow {
  color: #0ea5e9 !important;
  transform: translateX(3px);
}

.settings-card:hover .settings-card__action {
  color: #0ea5e9;
}

/* ---------------------------------------------------------------------------
   DARK — the card as a lifted surface, not an outlined box.

   The light rule draws the card with a pale 1px outline (rgba(226,232,240,.5)),
   which is invisible against white but on charcoal renders as a bright
   rectangle drawn around nothing — the outline ends up louder than the card.
   So dark separates the card the way the palette intends: the surface tier
   does the work (--app-card over --app-page), a 1px inner top highlight gives
   it an edge, and the shadow only softens that edge. The border stays in the
   box model but goes transparent, so hover can bring an accent outline in
   without shifting the layout by a pixel.
   --------------------------------------------------------------------------- */
.dark-mode .settings-card {
  border-color: transparent;
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 1px 3px var(--app-shadow-soft);
}

.dark-mode .settings-card:hover {
  background: var(--app-elevated);
  border-color: var(--app-accent-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 8px 24px var(--app-shadow);
}

/* The tile keeps its per-item accent wash (inline, 8% alpha); on charcoal that
   alone has no edge, so give it the same hairline the card gets. */
.dark-mode .settings-card__icon {
  box-shadow: inset 0 0 0 1px var(--app-inner-highlight);
}

.dark-mode .settings-card__arrow {
  color: var(--app-text-faint) !important;
}

.dark-mode .settings-card:hover .settings-card__arrow {
  color: var(--app-accent) !important;
}

.dark-mode .settings-card:hover .settings-card__action {
  color: var(--app-accent);
}

.dark-mode .settings-card:hover .settings-card__name {
  color: var(--app-text);
}

/* Mobile */
@media (max-width: 599px) {
  .settings-page {
    padding: 20px 16px !important;
  }

  .settings-header__title {
    font-size: 1.2rem;
  }

  .settings-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .settings-card__content {
    padding: 14px 16px;
    gap: 10px;
  }

  .settings-card__icon {
    width: 36px;
    height: 36px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .settings-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}
</style>
